/**
 * StatsBar component for Project Dashboard
 * Displays total, scale, fix, kill counts with visual badges
 */

import {
  BarChart3,
  TrendingUp,
  AlertTriangle,
  XCircle,
  Clock,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { Card, CardTitle } from '@/components/ui/Card';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export interface StatsBarProps {
  total: number;
  scale: number;
  fix: number;
  kill: number;
  pending: number;
  isLoading?: boolean;
}

interface StatItemProps {
  title: string;
  value: number;
  icon: ReactNode;
  badge?: {
    label: string;
    variant: 'success' | 'warning' | 'danger' | 'muted';
  };
  isLoading?: boolean;
}

// ============================================================================
// SKELETON COMPONENT
// ============================================================================

function StatItemSkeleton() {
  return (
    <Card variant="metric">
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <div className="h-3 w-24 bg-surface-subtle rounded animate-pulse" />
          <div className="h-8 w-16 bg-surface-subtle rounded animate-pulse" />
        </div>
        <div className="h-5 w-5 bg-surface-subtle rounded animate-pulse" />
      </div>
    </Card>
  );
}

// ============================================================================
// STAT ITEM COMPONENT
// ============================================================================

function StatItem({ title, value, icon, badge, isLoading }: StatItemProps) {
  const badgeStyles = {
    success: 'bg-success-soft text-success',
    warning: 'bg-warning-soft text-warning',
    danger: 'bg-danger-soft text-danger',
    muted: 'bg-surface-subtle text-muted',
  };

  if (isLoading) {
    return <StatItemSkeleton />;
  }

  return (
    <Card variant="metric">
      <div className="flex items-start justify-between">
        <div className="space-y-3 flex-1">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-end gap-3">
            <div className="text-3xl font-bold text-primary leading-tight tracking-tight">
              {value.toLocaleString()}
            </div>
            {badge && (
              <div
                className={cn(
                  'inline-flex items-center px-3 h-6 rounded-pill text-xs font-bold uppercase tracking-wider',
                  badgeStyles[badge.variant]
                )}
              >
                {badge.label}
              </div>
            )}
          </div>
        </div>
        <div className="text-secondary opacity-70 flex-shrink-0">{icon}</div>
      </div>
    </Card>
  );
}

// ============================================================================
// STATS BAR COMPONENT
// ============================================================================

export function StatsBar({
  total,
  scale,
  fix,
  kill,
  pending,
  isLoading = false,
}: StatsBarProps) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      <StatItem
        title="Total Creatives"
        value={total}
        icon={<BarChart3 className="h-5 w-5" />}
        isLoading={isLoading}
      />
      <StatItem
        title="To Scale"
        value={scale}
        icon={<TrendingUp className="h-5 w-5" />}
        badge={{ label: 'Scale', variant: 'success' }}
        isLoading={isLoading}
      />
      <StatItem
        title="To Fix"
        value={fix}
        icon={<AlertTriangle className="h-5 w-5" />}
        badge={{ label: 'Fix', variant: 'warning' }}
        isLoading={isLoading}
      />
      <StatItem
        title="To Kill"
        value={kill}
        icon={<XCircle className="h-5 w-5" />}
        badge={{ label: 'Kill', variant: 'danger' }}
        isLoading={isLoading}
      />
      <StatItem
        title="Not Analyzed"
        value={pending}
        icon={<Clock className="h-5 w-5" />}
        badge={{ label: 'Pending', variant: 'muted' }}
        isLoading={isLoading}
      />
    </div>
  );
}
